import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import Modal from 'antd/lib/modal';
import {Field, Form, Formik, FormikProps} from 'formik';

import * as Yup from 'yup';

import moment from 'moment';

import {FileInterface, FileType} from '@common/typescript/objects/FileInterface';

import candleImg from '@app/images/link-icons/candle.svg';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import SearchFieldAuto from '@app/components/Forms/SearchField/SearchFieldAuto';
import CustomDatePicker from '@app/components/Forms/CustomDatePicker/CustomDatePicker';
import CustomCheckbox from '@app/components/Forms/CustomCheckbox/CustomCheckbox';
import CustomAutoTokenization from '@app/components/Forms/CustomAutoTokenization/CustomAutoTokenization';
import {PetMemorialModalFormProps} from '@app/objects/PetMemorial';
import {rest} from '@app/components/Api';
import CustomSelect from '@app/components/Forms/CustomSelect/CustomSelect';
import {stringBeautifyValidator} from '@app/components/LocalCommon/Utils/Validators';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import {onFileUpload} from '@app/components/LocalCommon/Utils/onFileUpload';
import {GAeventSubmit} from '@app/components/LocalCommon/Utils/GAevents';
import FileUpload from '@app/components/LocalCommon/FileUpload/FileUpload';
import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';

import '@app/scss/localCommon/components/attachment.scss';
import PetMemorialPreviewModal from '@app/components/UI/PetMemorialModal/PetMemorialPreviewModal';
import VetTesPreviewModal from '@app/components/Pages/VetTestimonials/VetTesPreviewModal';
import {stateFilter} from '@app/components/LocalCommon/Utils/StateFilter';

const validationSchema = Yup.object({
	firstName: stringBeautifyValidator('First Name is a required field'),
	petName: stringBeautifyValidator('Pet\'s Name is a required field'),
	dateBirth: stringBeautifyValidator('Pet\'s Birthday is a required field'),
	datePass: stringBeautifyValidator('Pet\'s Date of Passing is a required field'),
	message: stringBeautifyValidator('Leave a message in memory of your pet is a required field'),
	story: stringBeautifyValidator('Tell us the \'story\' of your pet is a required field'),
	city: stringBeautifyValidator('City is a required field'),
	state: stringBeautifyValidator('State is a required field'),
	isAssist: stringBeautifyValidator('Did Lap of Love assist you is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	share: Yup.array().of(Yup.string().email('Please make sure all email addresses are entered correctly')),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const PetMemorialModalForm: React.FC<PetMemorialModalFormProps> = ({
		modalVisible,
		setModalVisible,
		stateListOptions,
		vetListOptions,
		setVetFieldValue,
		sent,
		setSent,
		setStateSelected
	}) => {
	const species = [
		{name: 'Dog'},
		{name: 'Cat'},
		{name: 'Other'}
	];

	const assistOptions = [
		{value: 0, label: 'No'},
		{value: 1, label: 'Yes'}
	];
	const toDate = (date: number) => {
		const dateObj = new Date(date);

		return dateObj.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'});
	};

	const [speciesSelected, setSpeciesSelected] = React.useState('');
	const [dateBirth, setDateBirth] = React.useState(0);
	const [isChecked, setIsChecked] = React.useState(true);
	const [attachment, setAttachment] = React.useState<FileInterface[]>([]);
	const maxFile = 10000000;
	const imgExtension = ['jpg', 'jpeg', 'png'];
	const maxImgCount = 6;
	const [stateFormlabel, setStateFormLabel] = React.useState<string | number | undefined>('');
	const [sending, setSending] = React.useState(false);
	const [image, setImage] = React.useState('');
	const [croppedBlob, setCroppedBlob] = React.useState({});
	const [open, setOpen] = React.useState<boolean>(false);
	const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);

	const [modalPreviewVisible, setModalPreviewVisible] = React.useState<boolean>(false);
	const [formValues, setFormValues] = React.useState({
		datePost: toDate(+new Date())
	});

	const openModal = (e) => {
		e.preventDefault();
		setImageAttach();
		setModalPreviewVisible(true);
	};

	const handleOnChange = (e) => {
		let name, value;

		if (e.target) {
			name = e.target.name;
			value = e.target.value;
		} else {
			name = e.name;
			value = e.value;
		}
		setFormValues(prevState => ({
			...prevState,
			[name]: value
		}));
	};
	const toStrictDate: (value: moment.Moment | null) => number | null = (value) => {
		return value && value.utcOffset(0).set({hour: 0, minute: 0, second: 0, millisecond: 0}).valueOf();
	};

	const setImageAttach = () => {
		const imagesAttached: string[] = [];
		attachment.forEach((item) => {
			imagesAttached.push(item.src);
		});

		setFormValues(prevState => ({
			...prevState,
			images: imagesAttached
		}));
	};

	React.useEffect(() => {
		setFormValues(prevState => ({
			...prevState,
			isShowName: isChecked
		}));
	}, [isChecked]);

	return (
		<>
		<Modal
			wrapClassName="pet-mem-modal pet-mem-modal_form modal-wrap"
			visible={modalVisible}
			onOk={() => setModalVisible(false)}
			onCancel={() => setModalVisible(false)}
			width={1037}
			centered
		>
			<div className="pet-mem-modal__content pet-mem-modal_form">
				<div className="pet-mem-modal__container pet-mem-modal_form__container">
					<header className="pet-mem-modal_form__header">
						<div className="pet-mem-modal_form__header-candle">
							<img src={candleImg} alt="candle"/>
						</div>

						{sent ? (
							<>
								<h2 className="pet-mem-modal_form__title pet-mem-modal_form__title_success">
									Thank you for submitting your memorial
								</h2>
								<h4 className="pet-mem-modal_form__subtitle ">
									We will post it within 5 business days and you will receive an email
									letting you know your angel's memorial is ready to view.
								</h4>
							</>
						) : (
							<>
								<h2 className="pet-mem-modal_form__title pet-mem-modal_form__title_desktop">
									Leave a memorial for a pet
								</h2>
								<h2 className="pet-mem-modal_form__title pet-mem-modal_form__title_mobile">
									Submit a memorial
								</h2>
								<h4 className="pet-mem-modal_form__subtitle pet-mem-modal_form__subtitle_desktop">
									Celebrate the life of your pet by sharing a story, detailing their life, <br/> or just
									writing about the beautiful bond you shared with your beloved friend.
								</h4>
							</>
						)}
					</header>
					{!sent ? (
						<Formik
							onSubmit={(values, formikActions) => {
								if (isConfirmed) {
									setSending(true);
									rest.post('v1/memorials', values)
										.then((response) => {
											setSent(true);
											setAttachment([]);
											formikActions.resetForm();
											setStateFormLabel('');
											GAeventSubmit('Memorial-Form', 'Submit', 'Memorial-Form');
											setSending(false);
										});
								}
							}}
							initialValues={{
								petName: '',
								species: '',
								dateBirth: '',
								datePass: '',
								message: '',
								story: '',
								firstName: '',
								lastName: '',
								isDisplayed: true,
								city: '',
								state: '',
								email: '',
								isAssist: '',
								vetId: '',
								vetTes: '',
								share: '',
								filePath: [],
								captcha: '',
							}}
							validationSchema={validationSchema}
							render={(formikBag: FormikProps<any>) => {
								return (
									<Form
										className="pet-mem-modal_form__form"
										autoComplete="new-password"
									>

										<h4>
											Pet information
										</h4>
										<div className="pet-mem-modal_form__form-pet-info">
											<CustomInput
												name="petName"
												placeholder="Pet's Name*"
												formikBag={formikBag}
												handleOnChange={handleOnChange}
											/>
											<CustomSelect
												name="species"
												placeholder="Species"
												onSelect={(value, option) => {
													formikBag.setFieldValue('species', value);
												}}
												onChange={(value, option) => {
													formikBag.setFieldValue('species', value);
													handleOnChange({name: 'species', value: option.value});
												}}
												onSearch={(value, option) => {
													formikBag.setFieldValue('species', value);
												}}
												options={species}
												showSearch={false}
												onFocus={() => {
												}}
												transform={true}
												formikBag={formikBag}

											/>

											<CustomDatePicker
												name="dateBirth"
												initValue="01/01/2020"
												placeholder="Pet’s Birthday*"
												onChange={(value, dateString) => {
													formikBag.setFieldValue('dateBirth', value ? toStrictDate(value) : dateString);
													handleOnChange({name: 'dateBirth', value: dateString});
												}}
												formikBag={formikBag}
											/>

											<CustomDatePicker
												name="datePass"
												initValue="01/01/2020"
												placeholder="Pet’s Date of Passing*"
												onChange={(value, dateString) => {
													formikBag.setFieldValue('datePass', value ? toStrictDate(value) : dateString);
													handleOnChange({name: 'datePass', value: dateString});
												}}
												formikBag={formikBag}
											/>

											<div className="vet-tes-modal__form-textarea">
												<CustomInput
													component={'textarea'}
													name="message"
													placeholder="Leave a message in memory of your pet*"
													formikBag={formikBag}
													handleOnChange={handleOnChange}
												/>
											</div>
											<div className="vet-tes-modal__form-textarea">
												<CustomInput
													component={'textarea'}
													name="story"
													placeholder="Tell us the ‘story’ of your pet*"
													formikBag={formikBag}
													handleOnChange={handleOnChange}
												/>
											</div>
										</div>

										<Field
											name="filePath"
											render={(field) => {
												return (
													<div className={field.form.errors['filePath'] && field.form.touched['filePath'] ? 'attachment attachment_cropped' : 'attachment'}>
														<h4 className="attachment-title">
															Upload pictures of your pet
														</h4>
														<AttachmentPreview
															attachment={attachment}
															setAttachment={setAttachment}
															formikBag={formikBag}
														/>
														<div className="attachment-upload-container">
															<FileUpload
																update={data => onFileUpload(
																	data,
																	field,
																	formikBag,
																	'filePath',
																	maxFile,
																	imgExtension,
																	attachment,
																	maxImgCount,
																	setAttachment,
																)}
																type="jobInquiry"
																fileType={FileType.File}
																noRelation={true}
																asButton buttonClassName="btn btn-secondary"
																buttonCaption="Upload a file"
																multiple={false}
																aspectRatio={16 / 9}
																maxFile={maxFile}
																field={field}
															/>
															<span className="attachment-note">(Up to 6 pictures, Maximum file size is 10MB in JPG or PNG format)</span>
														</div>
														{
															field.form.errors['filePath'] && field.form.touched['filePath'] ?
																<div className="custom-input__error error_top-gap"> {field.form.errors['filePath']}</div> : null
														}
													</div>
												);
											}}
										/>
										<h4>
											Personal information
										</h4>
										<div className="pet-mem-modal_form__form-personal-info">
											<CustomInput
												name="firstName"
												placeholder="First Name*"
												formikBag={formikBag}
												handleOnChange={handleOnChange}
											/>
											<CustomInput name="lastName" placeholder="Last Name" formikBag={formikBag} handleOnChange={handleOnChange}/>
											<CustomCheckbox
												isChecked={isChecked}
												setIsChecked={setIsChecked}
												formikBag={formikBag}
												capture="Would you like your name displayed with your memorial?"
											/>
											<CustomInput name="city" placeholder="City*" formikBag={formikBag} handleOnChange={handleOnChange}/>
											<SearchFieldAuto
												name="state"
												placeholder="State*"
												listOptions={stateListOptions}
												formikBag={formikBag}
												onSelect={(value, option) => {
													formikBag.setFieldValue('state', value);
													setStateFormLabel(option.label);
													setStateSelected(value);
												}}
												label={stateFormlabel}
												onChange={(value, option) => {
													formikBag.setFieldValue('state', value);
													setStateFormLabel(value);
													!value && setStateSelected('');
													handleOnChange({name: 'state', value: option.value});
												}}
												searchButton={false}
												adjustValue
												setStateFormLabel={setStateFormLabel}
												allowClear={true}
												customFilter={stateFilter}
											/>
											<CustomInput
												name="email"
												placeholder="Email Address*"
												formikBag={formikBag}
												removeSpace={true}
												handleOnChange={handleOnChange}
											/>
											<CustomSelect
												name="isAssist"
												placeholder="Did Lap of Love assist you?*"
												onSelect={(value, option) => {
													formikBag.setFieldValue('isAssist', value);
												}}
												onChange={(value, option) => {
													formikBag.setFieldValue('isAssist', value);
													handleOnChange({name: 'isAssist', value: option.value});
												}}
												onSearch={(value, option) => {
													formikBag.setFieldValue('isAssist', value);
												}}
												options={assistOptions}
												showSearch={false}
												onFocus={() => {
												}}
												formikBag={formikBag}
											/>
											<CustomSelect
												name="vetId"
												placeholder="Select or type Veterinarian Name"
												onSelect={(value, option) => {
													setOpen(false);
													formikBag.setFieldValue('vetId', value);
												}}
												onChange={(value, option) => {
													setOpen(false);
													formikBag.setFieldValue('vetId', value);
													handleOnChange({name: 'vetName', value: option.label});
												}}
												onSearch={(value, option) => {
													value.length > 0 ? setOpen(true) : setOpen(false);
													setVetFieldValue && setVetFieldValue(value);
												}}
												options={vetListOptions}
												showSearch={true}
												onFocus={() => {
												}}
												transform={true}
												formikBag={formikBag}
												parentContainer={'.pet-mem-modal_form__form-pet-info'}
												value={!formikBag.values['vetId'] ? undefined : formikBag.values['vetId']}
												open={open}
												showArrow={false}
											/>
											<div className="vet-tes-modal__form-textarea">
												<CustomInput
													component={'textarea'}
													name="vetTes"
													placeholder="Write a testimonial for your Lap of Love veterinarian"
													formikBag={formikBag}
													handleOnChange={handleOnChange}
												/>
											</div>
										</div>

										<div className="share">
											<h4>
												Would you like to share your memorial with friends or family?
											</h4>
											<CustomAutoTokenization
												name="share"
												placeholder="Email addresses of friends to share your memorial with"
												// @ts-ignore
												onChange = {(value) => {
													formikBag.setFieldValue('share', value);
												}}
											/>
										</div>
										<FormikField
											containerClassName="captcha"
											fieldName="captcha"
											render={fieldProps =>
												<ReCAPTCHA
													sitekey="6LezTMIcAAAAACaXvLNse5bmTJpwqs0Ezm_bhTrU"
													onChange={(value) => {
														if (value) {
															formikBag.setFieldValue('captcha', value);
															setIsConfirmed(true);
														} else {
															formikBag.setFieldValue('captcha', '');
															setIsConfirmed(false);
															setSending(false);
														}
													}}
													onExpired={() => formikBag.setFieldValue('captcha', 'expired')}
													hl="en"
												/>
											}
										/>
										<div className="modal-button">
											<button
												type="submit"
												className="btn btn-primary btn-lg"
												disabled={sending}
											>
												Submit your memorial
											</button>
											{
												sending ?
													<div className="lds-ring">
														<div/>
														<div/>
														<div/>
														<div/>
													</div>
													: null
											}

											<button
												type="button"
												className="btn btn-primary btn-lg"
												onClick={openModal}
											>
													Preview your memorial
											</button>
										</div>
									</Form>
								);
							}}
						/>
					) : (
						<>
						</>
					)}
				</div>
			</div>
		</Modal>
		<PetMemorialPreviewModal
			modalVisible={modalPreviewVisible}
			setModalVisible = {setModalPreviewVisible}
			memorial={formValues}
		/>
		</>
	);
};

export default PetMemorialModalForm;